import { useQuery } from '@tanstack/react-query';
import { OrderPackage } from '../models';
import QUERY_KEYS from '../utils/QueryKeys';

/**
 * Which add-ons are already in this customer's cart, and where to find them.
 *
 * ⚠️ FROM THE SERVER, NOT FROM COMPONENT STATE. The gifts shelf marks a product
 * "Added to order", and that badge has to survive a refresh, a Back, and a
 * return from the edit screen. Anything held in React state is gone on reload
 * and would tell the customer they had not bought something they had.
 *
 * Why order packages rather than the cart: `useCart` calls the cart SHOW
 * endpoint, which sideloads no order packages, and adding includes there would
 * change the payload for every other page that uses it. This asks the question
 * directly instead.
 *
 * ⚠️ FILTERED BY `student_id`, AND THAT IS REQUIRED, not decorative:
 * Api::OrderPackagesController#index raises RoutingError for a non-admin
 * without it. Dropping it would 404 for every real customer while working
 * perfectly in an admin session.
 *
 * `notPurchased` keeps finished orders out — a gift bought last season is not
 * in the cart now.
 */
/**
 * addonId -> where that add-on lives in THIS cart, so a tile can say "Added to
 * order" and its button can open the item to edit or remove it.
 *
 * ⚠️ NO CART ID MEANS NOTHING IS ADDED, not "everything matches". This used to
 * skip the filter entirely when `cartId` was falsy, and `cartId` is
 * `customer?.cart?.id` -- so a returning customer whose session has no cart yet,
 * but who still has unpurchased packages from an earlier one, saw "Added to
 * order" on products that are NOT in the cart they are filling. Claiming
 * something is in the cart when it is not is the worse of the two failures:
 * the badge simply appears once the cart id arrives, because it is part of the
 * query key.
 *
 * Exported so this is testable without standing up a react-query provider.
 */
export function addonSheetsByAddon(orderPackages, cartId) {
  const byAddon = new Map();

  (orderPackages || [])
    // A cart id arrives as a number from one place and a string from another;
    // compare loosely rather than lose every match to a type.
    .filter((op) => !!cartId && String(op.cartId) === String(cartId))
    .forEach((orderPackage) => {
      (orderPackage.addonSheets || []).forEach((sheet) => {
        const key = String(sheet.addonId);
        const existing = byAddon.get(key);

        // A sheet an OFFER granted (grantedByCouponId, #5747) counts separately:
        // the tile says "Free" for it, and a second copy the parent adds is paid.
        const granted = sheet.grantedByCouponId != null && sheet.grantedByCouponId !== '' ? 1 : 0;

        if (existing) {
          existing.sheetIds.push(sheet.id);
          existing.count += 1;
          existing.granted += granted;
          return;
        }

        byAddon.set(key, {
          orderPackageId: orderPackage.id,
          sheetIds: [sheet.id],
          count: 1,
          granted,
        });
      });
    });

  return byAddon;
}

export default function useCartAddonSheets({ studentId, cartId }) {
  return useQuery(
    [QUERY_KEYS.ORDER_PACKAGES, 'cartAddonSheets', studentId, cartId],
    () => (
      OrderPackage
        .where({ studentId, notPurchased: true })
        .includes('addonSheets')
        // ⚠️ PAST THE DEFAULT PAGE SIZE, deliberately. Graphiti pages at 20, and
        // a long-standing family can hold more unpurchased packages than that --
        // the test student has 22. Off the first page only, an add-on sitting on
        // package 21 would read "Customize" while it was already in the cart,
        // and pressing it would add a second one.
        .per(250)
        .all()
        .then((res) => res.data)
    ),
    {
      // Both ids, not just the student: with no cart id the answer is always
      // "nothing is added" (see addonSheetsByAddon), so firing the request is
      // wasted work -- it went out once with `cartId` undefined and again when
      // the id arrived. `cartId` is in the query key, so the badge still
      // appears the moment the cart resolves.
      enabled: !!studentId && !!cartId,
      // The shelf must not show a stale "Added to order" after the customer has
      // just added or removed something on another screen.
      refetchOnMount: 'always',
      select: (orderPackages) => addonSheetsByAddon(orderPackages, cartId),
    },
  );
}
